import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Switch,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
	actualizarConfiguracionCliente,
	actualizarTipoTicket,
	actualizarCategoriaTicket,
	clonarTiposTickets,
	crearTipoTicket,
	crearCategoriaTicket,
	eliminarCategoriaTicket,
	eliminarTipoTicket,
	getConfiguracionCliente,
	getCategoriasTickets,
	getTiposTickets,
} from '../../services/apis';

const emptyConfig = {
	id: null,
	nombre: '',
	descripcion_corta: '',
	nombre_mostrar: '',
	color_principal: '#1a202c',
	logo: null,
	logoPreview: '',
	eliminar_logo: 0,
};

const emptyTicket = {
	id: null,
	nombre: '',
	paga: 1,
	mostrar: 1,
	id_categoria_ticket: '',
};

const resizeLogoFile = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			const image = new Image();

			image.onload = () => {
				const maxWidth = 300;
				const scale = image.width > maxWidth ? maxWidth / image.width : 1;
				const canvas = document.createElement('canvas');
				canvas.width = Math.round(image.width * scale);
				canvas.height = Math.round(image.height * scale);

				const context = canvas.getContext('2d');
				context.drawImage(image, 0, 0, canvas.width, canvas.height);
				resolve(canvas.toDataURL('image/png'));
			};

			image.onerror = reject;
			image.src = reader.result;
		};

		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

export const Configuracion = () => {
	const navigate = useNavigate();
	const [config, setConfig] = useState(emptyConfig);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [tiposTickets, setTiposTickets] = useState([]);
	const [anyosTickets, setAnyosTickets] = useState([new Date().getFullYear()]);
	const [ticketForm, setTicketForm] = useState(emptyTicket);
	const [anyoTickets, setAnyoTickets] = useState(new Date().getFullYear());
	const [savingTicket, setSavingTicket] = useState(false);
	const [categoriasTickets, setCategoriasTickets] = useState([]);
	const [nombreCategoria, setNombreCategoria] = useState('');
	const [savingCategoria, setSavingCategoria] = useState(false);
	const [categoriaEditando, setCategoriaEditando] = useState(null);

	const cargarConfiguracion = useCallback(async () => {
		setLoaded(false);
		setError('');

		try {
			const response = await getConfiguracionCliente();

			if (response?.success && response.data) {
				setConfig({
					...emptyConfig,
					id: response.data.id,
					nombre: response.data.nombre || '',
					nombre_mostrar: response.data.nombre_mostrar || '',
					color_principal: response.data.color_principal || emptyConfig.color_principal,
					logo: null,
					logoPreview: response.data.logo || '',
				});
			} else if (response?.message === 'Rol no permitido.') {
				navigate('/');
			} else {
				setError(response?.message || "No s'ha pogut carregar la configuració.");
			}
		} catch (err) {
			console.error('Error en carregar la configuració:', err);
			setError('Error en carregar la configuració.');
		} finally {
			setLoaded(true);
		}
	}, [navigate]);

	const cargarTiposTickets = useCallback(async () => {
		try {
			const response = await getTiposTickets(-1, anyoTickets);
			if (response?.success) {
				setTiposTickets(response.data || []);
				setAnyosTickets(response.anys?.length ? response.anys : [new Date().getFullYear()]);
			}
		} catch (err) {
			console.error('Error en carregar tipus de tiquets:', err);
		}
	}, [anyoTickets]);

	const cargarCategoriasTickets = useCallback(async () => {
		try {
			const response = await getCategoriasTickets();
			if (response?.success) setCategoriasTickets(response.data || []);
		} catch (err) { console.error('Error en carregar categories de tiquets:', err); }
	}, []);

	useEffect(() => {
		cargarConfiguracion();
		cargarTiposTickets();
		cargarCategoriasTickets();
	}, [cargarConfiguracion, cargarTiposTickets, cargarCategoriasTickets]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setConfig((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleLogoChange = async (event) => {
		const file = event.target.files?.[0];

		if (!file) {
			return;
		}

		if (!file.type.startsWith('image/')) {
			setError('El logo ha de ser una imatge.');
			return;
		}

		try {
			const resizedLogo = await resizeLogoFile(file);
			setConfig((prev) => ({
				...prev,
				logo: resizedLogo,
				logoPreview: resizedLogo,
				eliminar_logo: 0,
			}));
		} catch (err) {
			console.error('Error en processar el logo:', err);
			setError("No s'ha pogut processar el logo.");
		}
	};

	const removeLogo = () => {
		setConfig((prev) => ({
			...prev,
			logo: '',
			logoPreview: '',
			eliminar_logo: 1,
		}));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const response = await actualizarConfiguracionCliente({
				nombre_mostrar: config.nombre_mostrar,
				color_principal: config.color_principal,
				logo: config.logo,
				eliminar_logo: config.eliminar_logo,
			});

			if (response?.success) {
				setSuccess('Configuració actualitzada correctament.');
				window.dispatchEvent(new Event('penya-auth-changed'));
				await cargarConfiguracion();
			} else {
				setError(response?.error || response?.message || "No s'ha pogut guardar la configuració.");
			}
		} catch (err) {
			console.error('Error en guardar la configuració:', err);
			setError('Error en guardar la configuració.');
		} finally {
			setSaving(false);
		}
	};

	const handleTicketChange = (event) => {
		const { name, value, checked, type } = event.target;
		setTicketForm((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
		}));
	};

	const editarTicket = (ticket) => {
		setTicketForm({
			id: ticket.id,
			nombre: ticket.nombre || '',
			descripcion_corta: ticket.descripcion_corta || '',
			paga: Number(ticket.paga || 0),
			mostrar: Number(ticket.mostrar || 0),
			id_categoria_ticket: ticket.id_categoria_ticket || '',
		});
	};

	const guardarTicket = async () => {
		if (!ticketForm.nombre.trim()) {
			setError('El nom del tiquet es obligatori.');
			return;
		}

		setSavingTicket(true);
		setError('');
		setSuccess('');

		try {
			const payload = {
				...ticketForm,
				anyo: anyoTickets,
				nombre: ticketForm.nombre.trim(),
				descripcion_corta: ticketForm.descripcion_corta.trim(),
				paga: Number(ticketForm.paga || 0),
				mostrar: Number(ticketForm.mostrar || 0),
			};
			const response = payload.id ? await actualizarTipoTicket(payload) : await crearTipoTicket(payload);

			if (response?.success) {
				setSuccess(payload.id ? 'Tipus de tiquet actualitzat correctament.' : 'Tipus de tiquet creat correctament.');
				setTicketForm(emptyTicket);
				await cargarTiposTickets();
			} else {
				setError(response?.message || "No s'ha pogut guardar el tipus de tiquet.");
			}
		} catch (err) {
			console.error('Error en guardar tipus de tiquet:', err);
			setError('Error en guardar el tipus de tiquet.');
		} finally {
			setSavingTicket(false);
		}
	};

	const clonarTicketsAnyAnterior = async () => {
		setSavingTicket(true);
		setError('');
		setSuccess('');

		try {
			const response = await clonarTiposTickets(anyoTickets, anyoTickets - 1);
			if (response?.success) {
				setSuccess('Tiquets clonats correctament.');
				setTicketForm(emptyTicket);
				await cargarTiposTickets();
			} else {
				setError(response?.message || "No s'han pogut clonar els tiquets.");
			}
		} catch (err) {
			console.error('Error en clonar tiquets:', err);
			setError('Error en clonar els tiquets.');
		} finally {
			setSavingTicket(false);
		}
	};

	const guardarCategoria = async () => {
		const nombre = nombreCategoria.trim();
		if (!nombre) { setError('El nom de la categoria es obligatori.'); return; }
		setSavingCategoria(true); setError(''); setSuccess('');
		try {
			const response = await crearCategoriaTicket(nombre);
			if (response?.success) { setNombreCategoria(''); setSuccess('Categoria creada correctament.'); await cargarCategoriasTickets(); }
			else setError(response?.message || "No s'ha pogut crear la categoria.");
		} finally { setSavingCategoria(false); }
	};

	const borrarCategoria = async (categoria) => {
		setSavingCategoria(true); setError(''); setSuccess('');
		try {
			const response = await eliminarCategoriaTicket(categoria.id);
			if (response?.success) { setSuccess('Categoria eliminada correctament.'); await cargarCategoriasTickets(); }
			else setError(response?.message || "No s'ha pogut eliminar la categoria.");
		} finally { setSavingCategoria(false); }
	};

	const actualizarCategoria = async () => {
		const nombre = categoriaEditando?.nombre?.trim();
		if (!nombre) { setError('El nom de la categoria es obligatori.'); return; }
		setSavingCategoria(true); setError(''); setSuccess('');
		try {
			const response = await actualizarCategoriaTicket(categoriaEditando.id, nombre);
			if (response?.success) { setCategoriaEditando(null); setSuccess('Categoria actualitzada correctament.'); await cargarCategoriasTickets(); await cargarTiposTickets(); }
			else setError(response?.message || "No s'ha pogut actualitzar la categoria.");
		} finally { setSavingCategoria(false); }
	};

	const desactivarTicket = async (ticket) => {
		setSavingTicket(true);
		setError('');
		setSuccess('');

		try {
			const response = await eliminarTipoTicket(ticket.id);
			if (response?.success) {
				setSuccess('Tipus de tiquet desactivat correctament.');
				if (Number(ticketForm.id) === Number(ticket.id)) {
					setTicketForm(emptyTicket);
				}
				await cargarTiposTickets();
			} else {
				setError(response?.message || "No s'ha pogut desactivar el tipus de tiquet.");
			}
		} catch (err) {
			console.error('Error en desactivar tipus de tiquet:', err);
			setError('Error en desactivar el tipus de tiquet.');
		} finally {
			setSavingTicket(false);
		}
	};

	return (
		<Box className="settings-shell fade-in-2">
			<Box className="settings-header">
				<Box>
					<Typography className="settings-eyebrow">Configuració</Typography>
					<Typography variant="h4" className="settings-title">
						{config.nombre_mostrar || 'Penya App'}
					</Typography>
				</Box>
			</Box>

			{error && (
				<Alert severity="error" className="settings-alert" onClose={() => setError('')}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" className="settings-alert" onClose={() => setSuccess('')}>
					{success}
				</Alert>
			)}

			{!loaded && (
				<Box className="settings-loading">
					<CircularProgress size={32} />
				</Box>
			)}

			{loaded && (
				<>
				<form onSubmit={handleSubmit} className="settings-grid">
					<Box className="settings-logo-card">
						<Box className="settings-logo-preview">
							{config.logoPreview ? (
								<img src={config.logoPreview} alt="Logo del client" />
							) : (
								<ImageOutlinedIcon />
							)}
						</Box>
						<Stack className="settings-logo-actions" direction="row">
							<Button variant="outlined" component="label" startIcon={<ImageOutlinedIcon />}>
								Pujar logo
								<input type="file" hidden accept="image/*" onChange={handleLogoChange} />
							</Button>
							<Button color="error" startIcon={<DeleteForeverIcon />} onClick={removeLogo} disabled={!config.logoPreview}>
								Llevar
							</Button>
						</Stack>
					</Box>

					<Box className="settings-colors-card">
						<Typography className="settings-section-title">Dades de l'app</Typography>

						<TextField
							label="Nom penya"
							name="nombre_mostrar"
							value={config.nombre_mostrar}
							onChange={handleChange}
							fullWidth
						/>

						<Box className="settings-color-field">
							<PaletteOutlinedIcon />
							<TextField label="Color principal" name="color_principal" value={config.color_principal} onChange={handleChange} fullWidth />
							<input type="color" name="color_principal" value={config.color_principal} onChange={handleChange} />
						</Box>

						<Box className="settings-preview">
							<span style={{ backgroundColor: config.color_principal }} />
						</Box>
						<Box className="settings-actions">
							<Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
								Guardar configuració
							</Button>
						</Box>
					</Box>
				</form>

				<Box className="settings-tickets-card">
					<Box className="settings-tickets-header">
						<Box>
							<Typography className="settings-section-title">Tipus de tiquets</Typography>
							<Typography color="text.secondary">
								Configura quins convidats es poden registrar i si paguen en menjars o activitats.
							</Typography>
						</Box>
						<Stack direction="row" spacing={1} alignItems="center">
							<TextField
								select
								label="Any"
								size="small"
								value={anyoTickets}
								onChange={(event) => {
									setAnyoTickets(Number(event.target.value) || new Date().getFullYear());
									setTicketForm(emptyTicket);
								}}
								className="settings-ticket-year"
							>
								{anyosTickets.map((anyo) => (
									<MenuItem key={anyo} value={anyo}>
										{anyo}
									</MenuItem>
								))}
							</TextField>
							<ConfirmationNumberOutlinedIcon />
						</Stack>
					</Box>

					{tiposTickets.length === 0 && (
						<Box className="settings-ticket-empty">
							<Typography color="text.secondary">
								No hi ha tipus de tiquets configurats per a {anyoTickets}.
							</Typography>
							<Button variant="outlined" onClick={clonarTicketsAnyAnterior} disabled={savingTicket}>
								Clonar tiquets de l'any passat
							</Button>
						</Box>
					)}

					<Grid container spacing={2} className="settings-ticket-form">
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField label="Nom del tiquet" name="nombre" value={ticketForm.nombre} onChange={handleTicketChange} fullWidth />
						</Grid>
						<Grid size={{ xs: 12, md: 3 }}>
							<TextField label="Descripció abreviada" name="descripcion_corta" value={ticketForm.descripcion_corta} onChange={handleTicketChange} fullWidth inputProps={{ maxLength: 30 }} helperText="Etiqueta per a taules i comptadors" />
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField select label="Categoria" name="id_categoria_ticket" value={ticketForm.id_categoria_ticket} onChange={handleTicketChange} fullWidth>
								<MenuItem value="">Sense categoria</MenuItem>
								{categoriasTickets.map((categoria) => <MenuItem key={categoria.id} value={categoria.id}>{categoria.nombre}</MenuItem>)}
							</TextField>
						</Grid>
						<Grid size={{ xs: 6, md: 1 }}>
							<FormControlLabel control={<Switch name="paga" checked={Number(ticketForm.paga) === 1} onChange={handleTicketChange} />} label="Paga" />
						</Grid>
						<Grid size={{ xs: 6, md: 1 }}>
							<FormControlLabel control={<Switch name="mostrar" checked={Number(ticketForm.mostrar) === 1} onChange={handleTicketChange} />} label="Actiu" />
						</Grid>
						<Grid size={{ xs: 12, md: 1 }}>
							<Button fullWidth variant="contained" startIcon={ticketForm.id ? <SaveIcon /> : <AddIcon />} onClick={guardarTicket} disabled={savingTicket}>
								{ticketForm.id ? 'Guardar' : 'Afegir'}
							</Button>
						</Grid>
					</Grid>

					<Stack className="settings-ticket-list">
						{tiposTickets.map((ticket) => (
							<Box key={ticket.id} className={`settings-ticket-row ${Number(ticket.mostrar) === 1 ? '' : 'settings-ticket-row-disabled'}`}>
								<Box>
									<Stack direction="row" spacing={1} alignItems="center">
									<Typography className="settings-ticket-name">{ticket.nombre}</Typography>
									{ticket.descripcion_corta && <Chip size="small" label={ticket.descripcion_corta} variant="outlined" />}
										<Chip size="small" label={ticket.categoria_nombre || 'Sense categoria'} color={ticket.categoria_nombre ? 'primary' : 'default'} variant={ticket.categoria_nombre ? 'filled' : 'outlined'} />
									</Stack>
									<Typography color="text.secondary">
										{Number(ticket.paga) === 1 ? 'Paga' : 'No paga'} · {Number(ticket.mostrar) === 1 ? 'Actiu' : 'Inactiu'}
									</Typography>
								</Box>
								<Stack direction="row" spacing={1}>
									<Button variant="outlined" onClick={() => editarTicket(ticket)}>
										Editar
									</Button>
									<IconButton color="error" onClick={() => desactivarTicket(ticket)} disabled={Number(ticket.mostrar) !== 1 || savingTicket}>
										<DeleteForeverIcon />
									</IconButton>
								</Stack>
							</Box>
						))}
						{tiposTickets.length === 0 && (
							<Typography color="text.secondary">Encara no hi ha tipus de tiquets configurats.</Typography>
						)}
					</Stack>

					<Box className="settings-tickets-categories">
						<Typography className="settings-section-title">Categories de tiquets</Typography>
						<Typography color="text.secondary">Les categories defineixen els comptadors que apareixen a cada esdeveniment.</Typography>
						<Stack direction="row" spacing={1} sx={{ mt: 2 }}>
							<TextField size="small" label="Nova categoria" value={nombreCategoria} onChange={(event) => setNombreCategoria(event.target.value)} fullWidth />
							<Button variant="outlined" onClick={guardarCategoria} disabled={savingCategoria}>Afegir</Button>
						</Stack>
						<Stack className="settings-ticket-list" sx={{ mt: 2 }}>
							{categoriasTickets.map((categoria) => {
								const editando = Number(categoriaEditando?.id) === Number(categoria.id);
								return <Box key={categoria.id} className="settings-ticket-row">
									{editando ? <TextField size="small" value={categoriaEditando.nombre} onChange={(event) => setCategoriaEditando((prev) => ({ ...prev, nombre: event.target.value }))} />
										: <Typography className="settings-ticket-name">{categoria.nombre}</Typography>}
									<Stack direction="row" spacing={1}>
										{editando ? <><Button size="small" onClick={actualizarCategoria} disabled={savingCategoria}>Guardar</Button><Button size="small" onClick={() => setCategoriaEditando(null)}>Cancel·lar</Button></>
											: <Button size="small" onClick={() => setCategoriaEditando({ id: categoria.id, nombre: categoria.nombre })}>Editar</Button>}
										<IconButton color="error" onClick={() => borrarCategoria(categoria)} disabled={savingCategoria || editando}><DeleteForeverIcon /></IconButton>
									</Stack>
								</Box>;
							})}
						</Stack>
					</Box>
				</Box>
				</>
			)}
		</Box>
	);
};
